<template>
  <div v-if="getPageVisibility('cycle-count')" class="cycle-count-container">
    <template v-if="!isRunningCycleCount">
      <portlet
          :icon="'fad fa-abacus'"
          :title="$t('shopfloor.menu.cycleCount')"
          class="mb-3"
      >
        <template slot="portletBody">
          <template>
            {{ $t('shopfloor.cyclecount.counting') }}:
            <template v-if="getAppStatus && getAppStatus.cyclecount && getAppStatus.cyclecount.length">
              <ul>
                <li v-bind:key="user.userId" v-for="user in getAppStatus.cyclecount">
                  {{ user.userName }}
                </li>
              </ul>
            </template>
            <template v-else>
              {{ $t('shopfloor.cyclecount.nobodyCounting') }}.
            </template>
            <button v-if="!(getAppStatus && getAppStatus.cyclecount && getAppStatus.cyclecount.length === 2)"
                    @click="startCycleCount()"
                    class="btn btn-primary btn-start-cycle-count btn-block">
              {{ $t('shopfloor.cyclecount.startCycleCount') }}
            </button>
          </template>
        </template>
      </portlet>
    </template>

    <template v-if="isRunningCycleCount && !isBusyDeletingStock">
      <div class="scan-grid">
        <button
                @click="showStopCycleCountDialog()"
                class="btn btn-primary btn-start-cycle-count btn-block">
          {{ $t('shopfloor.cyclecount.stopCycleCount') }}
        </button>

        <portlet
            :icon="'fad fa-scanner'"
            :title="$t('shopfloor.cyclecount.currentScan')"
            class="mb-3"
        >
          <template slot="portletBody">
            <div>
              <template v-if="!lastResponse">
                <p>{{ $t('shopfloor.cyclecount.nothingScanned') }}
                <div style="text-align: center; animation: blink 5s infinite; font-size: 8rem;"><span
                    class="far fa-barcode-read"></span></div>
                <p class="scan-instruction">{{ $t('shopfloor.cyclecount.scanProduct') }}</p>
                <div class="manual-barcode-input-bottom barcode-input">
                  <form @submit.prevent="barcodeScanned(manualBarcode)">
                    <label for="manual-barcode">{{ $t('shopfloor.cyclecount.manualBarcode') }}</label>
                    <div class="d-flex">
                      <input type="text" id="manual-barcode" class="form-control" v-model="manualBarcode"/>
                      <button class="btn btn-primary" type="submit">
                        <i class="fas fa-chevron-right"></i>
                      </button>
                    </div>
                  </form>
                </div>
              </template>
              <template v-if="lastResponse && lastResponse.message !== 'BARCODE_NOT_FOUND'">
                <div class="cycle-count-text"><span>{{ lastResponse.product.name }}</span>
                  <span>({{ lastResponse.product.productNumber }})</span>
                </div>
                <template v-if="lastResponse.outgoingCargo">
                  <div class="cycle-count-text"><span>{{ $t('shopfloor.cyclecount.amountLoaded') }}:</span><span>{{
                      lastResponse.amountLoaded
                    }} /
                {{ lastResponse.amountToLoadIncludingDeviations }}</span>
                  </div>
                </template>
                <template v-if="lastResponse.deviations && lastResponse.deviations.length ">
                  <div class="cycle-count-text"><span>{{ $t('shopfloor.cyclecount.deviations') }}:</span>
                    <ul>
                      <li v-bind:key="deviation.type"
                          v-for="deviation in getGroupedDeviations(lastResponse.deviations)">
                        {{ $t('shopfloor.cyclecount.deviation.' + deviation.type) }} ({{ deviation.amountSum }})
                      </li>
                    </ul>
                  </div>
                </template>
                <template v-else>
                  <div class="cycle-count-text"><span>{{ $t('shopfloor.cyclecount.deviations') }}:</span> <span
                      class="no-data">{{ $t('shopfloor.cyclecount.noDeviations') }}</span></div>
                </template>
                <template v-if="lastResponse.outgoingCargo">
                  <div class="cycle-count-text"><span>{{
                      $t('shopfloor.cyclecount.outgoingCargo')
                    }}:</span><span>{{ lastResponse.outgoingCargo.reference }}
                  ({{ etdDateToString(lastResponse.outgoingCargo.estimatedTimeOfDeparture) }})</span>
                  </div>
                </template>
                <template v-else>
                  <div class="cycle-count-text"><span>{{
                      $t('shopfloor.cyclecount.outgoingCargo')
                    }}:</span><span>{{ $t('shopfloor.cyclecount.noOutgoingCargo') }} </span></div>
                </template>
                <div class="cycle-count-text"><span v-if="lastResponse.locations && lastResponse.locations.length">{{
                    $t('shopfloor.cyclecount.locations')
                  }}:</span> <span
                    v-if="!lastResponse.locations || !lastResponse.locations.length"
                    class="not-unloaded">{{ $t('shopfloor.cyclecount.noLocations') }}</span></div>
                <template v-if="lastResponse.locations && lastResponse.locations.length">
                  <div class="cycle-count-text">
                    <ul>
                      <li v-bind:key="location.id" v-for="location in lastResponse.locations">{{ location.name }}
                        ({{ location.stock }})
                      </li>
                    </ul>
                  </div>
                </template>
                <div style="text-align: center; animation: blink 5s infinite; font-size: 8rem;"><span
                    class="far fa-barcode-read"></span></div>
                <p class="scan-instruction">{{ $t('shopfloor.cyclecount.scanLocation') }}</p>
                <button class="btn-cancel-scan btn btn-primary btn-outline-filter" @click="cancelScan()" type="button">
                  <span>{{ $t('shopfloor.cyclecount.cancelScan', {productNumber: this.checkingBarcode}) }}</span>
                </button>
              </template>
            </div>
          </template>
        </portlet>

        <portlet
            :icon="'fad fa-inventory'"
            :title="$t('shopfloor.cyclecount.previousScannedProducts')"
            class="mb-3"
        >
          <template slot="portletBody">
            <div>
              <table>
                <thead>
                <tr>
                  <th>{{ $t('shopfloor.cyclecount.productNumber') }}</th>
                  <th>{{ $t('shopfloor.cyclecount.productName') }}</th>
                  <th>{{  $t('shopfloor.cyclecount.locationName')  }}</th>
                  <th>{{  $t('shopfloor.cyclecount.stock')  }}</th>
                  <th></th>
                </tr>
                </thead>
                <tbody>
                <tr :class="index % 2 ? 'light-row' : 'dark-row'" v-bind:key="index" v-for="(scannedProduct, index) in scannedProducts">
                  <td :label="$t('shopfloor.cyclecount.productNumber')">{{ scannedProduct.productNumber }}</td>
                  <td :label="$t('shopfloor.cyclecount.productName')">{{ scannedProduct.productName }}</td>
                  <td :label="$t('shopfloor.cyclecount.locationName')">{{ scannedProduct.locationName }}</td>
                  <td :label="$t('shopfloor.cyclecount.stock')">{{ scannedProduct.stock }}</td>
                  <td :label="$t('button.cancel')"><a @click="decrementStock(scannedProduct)" class="delete undo-scan action-icon dt-router-link"><i
                      title="Verwijder" class="far fa-minus-circle delete"></i></a></td>
                </tr>
                <tr v-if="!scannedProducts.length">
                  <td class="no-result" colspan="5">
                    Geen resultaten aanwezig in de tabel
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </template>
        </portlet>
      </div>
    </template>

    <template v-if="isRunningCycleCount && isBusyDeletingStock">
      <portlet
          :icon="'fad fa-abacus'"
          :title="$t('shopfloor.menu.cycleCount')"
          class="mb-3"
      >
        <template slot="portletBody">
          <div>{{ $t('shopfloor.cyclecount.clearingStock') }}</div>
        </template>
      </portlet>
    </template>
  </div>
  <div class="not-allowed" v-else>
    👮🚫
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";

export default {
  name: "CycleCount",
  data() {
    return {
      checkingBarcode: '',
      manualBarcode: '',
      lastResponse: null,
      scannedProducts: [],
      incrementPromises: [],
      decrementPromises: []
    };
  },
  filters: {},
  computed: {
    ...mapGetters('users', [
      'getMeBasic'
    ]),
    ...mapGetters('locations', ['getLocation']),
    ...mapGetters('appstatus', [
      'getAppStatus'
    ]),
    ...mapGetters('userData', [
      'getPageVisibility'
    ]),
    isRunningCycleCount() {
      const currentAppStatus = this.getAppStatus;
      if (currentAppStatus && currentAppStatus.cyclecount) {
        const started = _.find(currentAppStatus.cyclecount, counter => {
          return counter.userId === this.getMeBasic.userId;
        });
        return !!started;
      }
      return false;
    },
    isBusyDeletingStock() {
      return this.getAppStatus && this.getAppStatus.busyDeletingStock;
    }
  },
  methods: {
    ...mapActions('unloading', [
      'checkIfBarcodeIsLocation'
    ]),
    etdDateToString(etdDate) {
      return moment(etdDate).format('DD/MM HH:mm');
    },
    cancelScan() {
      this.lastResponse = null;
      this.manualBarcode = '';
      this.checkingBarcode = '';
    },
    async barcodeScanned(barcode) {
      if (!this.lastResponse) {
        this.checkingBarcode = barcode;
        this.$store.commit('setLoading', true);
        this.lastResponse = await this.checkBarcode(barcode);
        this.$store.commit('setLoading', false);
        if (!this.lastResponse) {
          this.showSomethingWentWrongToast();
        } else if (this.lastResponse.message === 'BARCODE_NOT_FOUND') {
          this.showUnknownBarcodeToast();
          this.lastResponse = null;
        }
      } else {
        const isLocation = await this.checkIfBarcodeIsLocation(barcode);
        if (isLocation) {
          this.submitStock(this.checkingBarcode, barcode);
          this.processResponse({ productNumber: _.cloneDeep(this.checkingBarcode), locationId: barcode, stock: 1 }, 1);
          this.cancelScan();
        } else {
          console.error('Is not a location');
        }
      }
    },
    async stopCycleCount() {
      const currentAppStatus = this.getAppStatus;
      const indexToRemove = _.findIndex(currentAppStatus.cyclecount, counter => {
        return counter.userId === this.getMeBasic.userId;
      });
      console.log("indexToRemove", indexToRemove, currentAppStatus.cyclecount, this.getMeBasic)
      currentAppStatus.cyclecount.splice(indexToRemove, 1);
      console.log("patch", currentAppStatus)
      await this.$store.dispatch('appstatus/patch', currentAppStatus);
      this.$store.commit('setLoading', true);
      // Increment the stock
      for(let i = 0; i < this.incrementPromises.length; i++) {
        try {
          let token = JSON.parse(localStorage.getItem('motivate-token')).oauthToken;
          await this.$CLOUD_API.post(this.incrementPromises[i], {
            headers: {
              Authorization: 'Bearer ' + token
            }
          });
        } catch (e) {
          console.error(e);
        }
      }
      // Decrement again
      for(let i = 0; i < this.decrementPromises.length; i++) {
        try {
          let token = JSON.parse(localStorage.getItem('motivate-token')).oauthToken;
          await this.$CLOUD_API.post(this.decrementPromises[i], {
            headers: {
              Authorization: 'Bearer ' + token
            }
          });
        } catch (e) {
          console.error(e);
        }
      }

      // Trigger the stock-updated trigger
      const uniqueProductNumbersScanned = _.uniqBy(this.scannedProducts, 'productNumber');
      for (let i = 0; i < uniqueProductNumbersScanned.length; i++) {
        try {
          let token = JSON.parse(localStorage.getItem('motivate-token')).oauthToken;
          await this.$CLOUD_API.post(`/cyclecount/stock-updated/${uniqueProductNumbersScanned[i].productNumber}`, {
            headers: {
              Authorization: 'Bearer ' + token
            }
          });
        } catch (e) {
          console.error(e);
        }
      }

      this.decrementPromises = [];
      this.incrementPromises = [];
      this.scannedProducts = [];
      this.$store.commit('setLoading', false);

      return true;
    },
    async showStopCycleCountDialog() {
      const swalResult = await this.$swal({
        title: this.$t('shopfloor.cyclecount.stopCycleCountDialog.title'),
        html: this.$t('shopfloor.cyclecount.stopCycleCountDialog.message'),
        showCancelButton: true,
        showConfirmButton: true,
        cancelButtonText: this.$t('shopfloor.cyclecount.stopCycleCountDialog.dismiss'),
        confirmButtonText: this.$t('shopfloor.cyclecount.stopCycleCountDialog.confirm'),
        reverseButtons: true
      });
      if (swalResult.value) {
        return this.stopCycleCount();
      } else {
        return Promise.resolve(false);
      }
    },
    processResponse(response, amount) {
      // response = { locationId: string, locationName: string, orderProductId: number,
      //              pickLocationId: number, productLocationId: number, productName: string, productNumber: string, stock: number }
      const indexOfProduct = _.findIndex(this.scannedProducts, scannedProduct => {
        return scannedProduct.locationId === response.locationId && scannedProduct.productNumber === response.productNumber
      });
      if (indexOfProduct === -1) {
        response.productName = _.cloneDeep(this.lastResponse.product.name);
        response.locationName = this.getLocation(response.locationId).name;
        this.scannedProducts.push(response);
      } else {
        this.scannedProducts[indexOfProduct].stock = this.scannedProducts[indexOfProduct].stock + amount;
        if (this.scannedProducts[indexOfProduct].stock === 0) {
          this.scannedProducts.splice(indexOfProduct, 1);
        }
      }
    },
    decrementStock(scannedProduct) {
      try {
        this.decrementPromises.push(`/cyclecount/decrement/${scannedProduct.productNumber}/${scannedProduct.locationId}`);
        this.processResponse(scannedProduct, -1);
      } catch (e) {
        this.showSomethingWentWrongToast();
        console.error(e);
        return null;
      }
    },
    showSomethingWentWrongToast() {
      this.$swal({
        customClass: {
          header: 'align-items-center'
        },
        toast: true,
        position: 'bottom-end',
        showConfirmButton: false,
        timer: 5000,
        type: 'error',
        title: this.$t('shopfloor.cyclecount.requestFailed')
      });
    },
    showUnknownBarcodeToast() {
      this.$swal({
        customClass: {
          header: 'align-items-center'
        },
        toast: true,
        position: 'bottom-end',
        showConfirmButton: false,
        timer: 5000,
        type: 'error',
        title: this.$t('shopfloor.cyclecount.unknownBarcode')
      });
    },
    async checkBarcode(barcode) {
      let token = JSON.parse(localStorage.getItem('motivate-token')).oauthToken;
      try {
        const res = await this.$CLOUD_API.get(`/loodsopname/${barcode}`, {
          headers: {
            Authorization: 'Bearer ' + token
          }
        });
        return res.data;
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    submitStock(barcode, locationId) {
      try {
        this.incrementPromises.push(`/cyclecount/${barcode}/${locationId}`);
      } catch (e) {
        console.error(e);
        return null;
      }
    },
    async startCycleCount() {
      const confirmed = await this.showStartCycleCountPopUp();
      if (confirmed) {
        await this.addUserToCounters();
      }
    },
    async addUserToCounters() {
      const currentAppStatus = this.getAppStatus;
      currentAppStatus.cyclecount.push(this.getMeBasic);
      if (currentAppStatus.cyclecount.length === 1) {
        currentAppStatus.busyDeletingStock = true;
      }
      return this.$store.dispatch('appstatus/patch', currentAppStatus);

    },
    async showStartCycleCountPopUp() {
      const swalResult = await this.$swal({
        title: this.$t('shopfloor.cyclecount.startingCycleCount'),
        html: this.$t('shopfloor.cyclecount.startingDialog'),
        showCancelButton: true,
        showConfirmButton: true,
        cancelButtonText: this.$t('shopfloor.cyclecount.startingDialogCancel'),
        confirmButtonText: this.$t('shopfloor.cyclecount.startingDialogConfirm'),
        reverseButtons: true
      });
      if (!swalResult.value) {
        return false;
      } else {
        return true;
      }
    }
  },
  created() {
    this.$barcodeScanner.barCodeEventBus.$on('barcodeScanned', this.barcodeScanned);
  },
  beforeDestroy() {
    this.$barcodeScanner.barCodeEventBus.$off('barcodeScanned', this.barcodeScanned);
  }
}
</script>

<style scoped lang="scss">

.cycle-count-container {
  padding: 1rem;
}

.btn-start-cycle-count {
  margin-top: 2rem;
}

.scan-grid {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto auto;
  grid-row-gap: 2rem;
}

ul {
  margin-bottom: 0px;
}

.cycle-count-text {
  margin-bottom: 1rem;
}

.cycle-count {
  margin-top: 15px;
}

.cycle-count > .m-portlet > .m-portlet__head > .m-portlet__head-caption > .m-portlet__head-title > .m-portlet__head-text {
  color: #00c9ff !important;
}

.scan-instruction {
  font-weight: bold;
  text-align: center;
}

.product-is-ok {
  color: #42dd00;
  text-align: center;
  font-size: 7rem;
}

.btn-cancel-scan {
  width: 100%;
  white-space: normal;
}

.not-unloaded {
  color: red;
}

table {
  width: 100%;
}

@media only screen and (max-width: 760px),
(min-device-width: 768px) and (max-device-width: 1024px) {

  /* Force table to not be like tables anymore */
  table, thead, tbody, th, td, tr {
    display: block;
  }

  /* Hide table headers (but not display: none;, for accessibility) */
  thead tr {
    position: absolute;
    top: -9999px;
    left: -9999px;
  }

  tr {
    border: 1px solid #ccc;
  }

  td {
    /* Behave  like a "row" */
    border: none;
    border-bottom: 1px solid #eee;
    position: relative;
    padding-left: 40%;
    overflow-wrap: anywhere;
  }

  td:before {
    /* Now like a table header */
    position: absolute;
    left: 6px;
    padding-right: 10px;
    white-space: nowrap;
  }

  /*
  Label the data
  */

  td:before {
    color: #00C9FF;
    content: attr(label);
  }
}

.undo-scan > i {
  color: red;
  cursor: pointer;
}

.light-row {
  padding-top: 1rem;
  padding-bottom: 1rem;
}
.dark-row {
  background: #f1f1f1;
  padding-top: 1rem;
  padding-bottom: 1rem;
}

.no-result {
  padding-left: 0px;
}
.not-allowed {
  font-size: 10rem;
  text-align: center;
}
</style>